import { useIsFetching, useIsMutating, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ContractorPortalApp } from "./ContractorPortalApp";
import { DocumentApp } from "./DocumentApp";
import { OAuthConsent } from "./OAuthConsent";
import { PortalApp } from "./PortalApp";
import { SignIn } from "./SignIn";
import { api, unwrap } from "./api";
import { signOut } from "./auth-client";
import { Admin } from "./pages/Admin";
import { AssistantGuide } from "./pages/AssistantGuide";
import { Billing } from "./pages/Billing";
import { ClientDetail } from "./pages/ClientDetail";
import { Clients } from "./pages/Clients";
import { Commissions } from "./pages/Commissions";
import { ContractDetail } from "./pages/ContractDetail";
import { ContractorDetail } from "./pages/ContractorDetail";
import { Contractors } from "./pages/Contractors";
import { Contracts } from "./pages/Contracts";
import { CostCodes } from "./pages/CostCodes";
import { EstimateDetail } from "./pages/EstimateDetail";
import { Estimates } from "./pages/Estimates";
import { Expenses } from "./pages/Expenses";
import { NewCompany } from "./pages/NewCompany";
import { Payables } from "./pages/Payables";
import { ProjectDetail } from "./pages/ProjectDetail";
import { Projects } from "./pages/Projects";
import { Reports } from "./pages/Reports";
import { Settings } from "./pages/Settings";

/** Shapes come from the API's route table, so they can't drift out of sync. */
type Me = Awaited<ReturnType<typeof fetchMe>>;
type CompanySummary = Me["companies"][number];

async function fetchMe() {
  return unwrap(await api.api.me.$get());
}

const TABS = [
  "reports",
  "projects",
  "billing",
  "payables",
  "estimates",
  "proposals",
  "contracts",
  "commissions",
  "expenses",
  "clients",
  "contractors",
  "cost-codes",
  "settings",
] as const;
type Tab = (typeof TABS)[number];

/** `contracts` rows come in two kinds — see `packages/db/src/schema/contracts.ts`
 *  — and each gets its own top-level tab: "proposals" for the offer, "contracts"
 *  for the binding document a proposal converts into. Both tabs render the same
 *  `ContractDetail`/`Contracts` components, parameterized by which kind. */
type ContractDocKind = "proposal" | "contract";

/** Sentinel option value in the company switcher. Not a slug — slugs can't
 * contain a space, so this can never collide with a real company. */
const NEW_COMPANY = "new company";

const TAB_LABELS: Record<Tab, string> = {
  reports: "Reports",
  projects: "Projects",
  billing: "Billing",
  payables: "Contractors AP",
  estimates: "Estimating",
  proposals: "Proposals",
  contracts: "Contracts",
  commissions: "Commissions",
  expenses: "Expenses",
  clients: "Clients",
  contractors: "Contractors",
  "cost-codes": "Cost codes",
  settings: "Settings",
};

/**
 * The company slug lives in the path: /c/acme/projects, a project's own id
 * follows the tab, and its section follows that:
 * /c/acme/projects/<uuid>/expenses. A drill-down that isn't in the URL can't
 * be linked, bookmarked, or reloaded, which is most of what an operator does
 * with one. A contractor's id follows the tab the same way under payables —
 * /c/acme/payables/<uuid> — and a client's under billing — /c/acme/billing/<uuid>
 * — since neither drill-down has sections of its own.
 */
function routeFromPath(): {
  slug: string | null;
  tab: Tab;
  projectId: string | null;
  section: string | null;
  contractorId: string | null;
  clientId: string | null;
  estimateId: string | null;
  contractId: string | null;
  admin: boolean;
} {
  // The operator console is not company-scoped, so it sits beside /c/:slug
  // rather than inside it.
  if (window.location.pathname.startsWith("/admin")) {
    return {
      slug: null,
      tab: "projects",
      projectId: null,
      section: null,
      contractorId: null,
      clientId: null,
      estimateId: null,
      contractId: null,
      admin: true,
    };
  }
  const m = window.location.pathname.match(
    /^\/c\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?/,
  );
  const tab = TABS.find((t) => t === m?.[2]) ?? "projects";
  const onProject = tab === "projects" && !!m?.[3];
  const onContractor = tab === "payables" && !!m?.[3];
  const onClient = tab === "billing" && !!m?.[3];
  const onEstimate = tab === "estimates" && !!m?.[3];
  const onContract = (tab === "proposals" || tab === "contracts") && !!m?.[3];
  return {
    slug: m?.[1] ?? null,
    tab,
    projectId: onProject ? m[3]! : null,
    section: onProject ? (m[4] ?? null) : null,
    contractorId: onContractor ? m[3]! : null,
    clientId: onClient ? m[3]! : null,
    estimateId: onEstimate ? m[3]! : null,
    contractId: onContract ? m[3]! : null,
    admin: false,
  };
}

export function App() {
  // The client portal and the contractor portal are each a fully separate
  // identity from staff — neither has an `/api/me` row, a company, or a
  // membership. Splitting here, before any staff-only hook runs, is what
  // keeps all three from ever touching; a branch further down (the way
  // `/admin` works) would still fire the staff `me` query first, which
  // neither portal session can answer.
  if (window.location.pathname.startsWith("/portal")) return <PortalApp />;
  if (window.location.pathname.startsWith("/contractor-portal")) return <ContractorPortalApp />;
  // A shared document has no identity at all behind it — the token in the path
  // is the whole authorisation. Same reasoning as the two portals: branching
  // here, before any staff hook runs, is what keeps a reader with no account
  // from ever firing the `me` query they cannot answer.
  if (window.location.pathname.startsWith("/d/")) return <DocumentApp />;
  // The OAuth consent screen. Before the shell because it has to render for a
  // visitor who is not signed in yet — it shows the sign-in screen itself and
  // returns here, rather than bouncing through the company switcher.
  if (window.location.pathname.startsWith("/oauth/authorize")) return <OAuthConsent />;
  // How to use a connected assistant. Branched here, with the pages that need
  // no identity, because the whole point of it is to be a link an owner can
  // send to somebody who has no reason to sign in — an estimator who will use
  // the assistant and never open Settings.
  if (window.location.pathname.startsWith("/assistant")) return <AssistantGuide />;

  const initial = routeFromPath();
  const [slug, setSlug] = useState<string | null>(initial.slug);
  const [tab, setTab] = useState<Tab>(initial.tab);
  const [projectId, setProjectId] = useState<string | null>(initial.projectId);
  const [section, setSection] = useState<string | null>(initial.section);
  const [contractorId, setContractorId] = useState<string | null>(initial.contractorId);
  const [clientId, setClientId] = useState<string | null>(initial.clientId);
  const [estimateId, setEstimateId] = useState<string | null>(initial.estimateId);
  const [contractId, setContractId] = useState<string | null>(initial.contractId);
  const [admin, setAdmin] = useState(initial.admin);
  // Opened from the company switcher. Not a route: creating a tenant is a rare
  // deliberate act, not somewhere you navigate back to.
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  // Back out of a project, or out of one of its sections, the way the browser
  // button says you can.
  useEffect(() => {
    function onPop() {
      const next = routeFromPath();
      if (next.slug) setSlug(next.slug);
      setTab(next.tab);
      setProjectId(next.projectId);
      setSection(next.section);
      setContractorId(next.contractorId);
      setClientId(next.clientId);
      setEstimateId(next.estimateId);
      setContractId(next.contractId);
      setAdmin(next.admin);
    }
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // A 401 here just means "not signed in yet" — the normal state on first
  // load — not a failure worth interrupting the sign-in screen with a toast.
  const me = useQuery({ queryKey: ["me"], queryFn: fetchMe, meta: { silent: true } });

  // Only fetched for a super user, and only to populate the switcher — the
  // companies they *belong to* still come from /api/me.
  const allCompanies = useQuery({
    queryKey: ["admin-companies"],
    enabled: me.data?.user.isSuperUser === true,
    queryFn: async () => unwrap(await api.api.admin.companies.$get()),
  });

  const isSuper = me.data?.user.isSuperUser === true;
  const memberOf = me.data?.companies ?? [];

  // A super user can switch into any tenant. Their own memberships come first
  // so the common case stays at the top of the list.
  const companies = isSuper
    ? [
        ...memberOf,
        ...(allCompanies.data?.companies ?? [])
          .filter((c) => !memberOf.some((m) => m.id === c.id) && !c.archivedAt)
          .map((c) => ({ ...c, role: "owner" as const })),
      ]
    : memberOf;

  const active = companies.find((c) => c.slug === slug) ?? companies[0];
  const visiting = !!active && !memberOf.some((m) => m.id === active.id);
  const resolvedSlug = active?.slug ?? null;

  /**
   * Keep the address bar honest about which company is on screen.
   *
   * `active` falls back to the first reachable company when the path names
   * none — landing on `/` is the common way in. Left unsynced, the URL says
   * nothing and the switcher quietly reads whichever company sorts first,
   * which is how an operator ends up inviting people into the wrong tenant
   * while believing they are somewhere else. Writing the slug back makes the
   * two agree, and `replaceState` keeps it out of the back button since it
   * isn't a navigation the user made.
   */
  useEffect(() => {
    if (admin || !resolvedSlug) return;
    const path = window.location.pathname;
    if (path.startsWith(`/c/${resolvedSlug}/`) || path === `/c/${resolvedSlug}`) return;
    window.history.replaceState({}, "", `/c/${resolvedSlug}/${tab}`);
    setSlug(resolvedSlug);
  });

  if (me.isPending) return <Centered>Loading…</Centered>;

  // A 401 means "not signed in", which is a screen, not an error state.
  if (me.isError) return <SignIn onSignedIn={() => queryClient.invalidateQueries()} />;

  /**
   * The operator console stands on its own — no company is selected, and
   * nothing it renders assumes one, so it gets a plain header rather than the
   * company shell below.
   */
  if (admin && isSuper && !creating) {
    return (
      <div className="min-h-screen bg-neutral-50 text-neutral-900">
        <header className="sticky top-0 z-30 h-14 border-b border-neutral-200 bg-white/85 backdrop-blur-md">
          <GlobalProgressBar />
          <div className="mx-auto flex h-full max-w-[1600px] items-center gap-4 px-6">
            <span className="whitespace-nowrap font-[family-name:--font-display] text-lg font-bold tracking-tight text-neutral-900">
              Dantria
            </span>
            <button
              type="button"
              className="rounded-lg px-2 py-1 text-sm text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
              onClick={() => {
                setAdmin(false);
                if (active) go(active.slug, tab);
                else window.history.pushState({}, "", "/");
              }}
            >
              ← {active ? active.name : "Back"}
            </button>
            <span className="ml-auto truncate text-sm text-neutral-500">{me.data.user.email}</span>
            <SignOutButton queryClient={queryClient} className="whitespace-nowrap" />
          </div>
        </header>
        <main className="mx-auto min-w-0 max-w-[1600px] px-6 py-8">
          <Admin
            onOpenCompany={(s) => {
              setAdmin(false);
              go(s, "reports");
            }}
            onCreateCompany={() => setCreating(true)}
          />
        </main>
      </div>
    );
  }

  if (isSuper && (creating || !active)) {
    return (
      <NewCompany
        email={me.data.user.email}
        onCreated={(created) => {
          // `go` is a function declaration, so it is hoisted past the early
          // returns below and clears the project drill-down along with the rest.
          go(created.slug, "reports");
          setCreating(false);
          // Reachable from the admin console now too — land in the new
          // company's shell rather than bouncing back to the console.
          setAdmin(false);
          // Both lists: the new company has no members, so it arrives through
          // the admin list rather than /api/me.
          queryClient.invalidateQueries({ queryKey: ["me"] });
          queryClient.invalidateQueries({ queryKey: ["admin-companies"] });
        }}
        onCancel={active ? () => setCreating(false) : undefined}
        footer={active ? undefined : <SignOutButton queryClient={queryClient} className="mt-4" />}
      />
    );
  }

  // Signed in, invited to nothing. Creating a company is a super-user action,
  // so there is nothing for this account to do but wait for an invitation —
  // saying so is better than a form that would 404.
  if (!active) {
    return (
      <Centered>
        <h1 className="font-[family-name:--font-display] text-xl font-semibold tracking-tight">
          No company yet
        </h1>
        <p className="mx-auto mt-2 max-w-sm text-sm text-neutral-500">
          You're signed in as {me.data.user.email} but don't belong to a company. Ask an owner or
          administrator for an invite link.
        </p>
        <SignOutButton queryClient={queryClient} className="mt-4 inline-block" />
      </Centered>
    );
  }

  // The third segment is a project id under "projects", a contractor id under
  // "payables", and a client id under "billing" — whichever the tab is, so one
  // function builds every drill-down URL rather than each needing its own
  // navigator.
  function go(
    nextSlug: string,
    nextTab: Tab,
    nextDetailId: string | null = null,
    nextSection: string | null = null,
  ) {
    const detail = nextDetailId ? `/${nextDetailId}${nextSection ? `/${nextSection}` : ""}` : "";
    window.history.pushState({}, "", `/c/${nextSlug}/${nextTab}${detail}`);
    setSlug(nextSlug);
    setTab(nextTab);
    setProjectId(nextTab === "projects" ? nextDetailId : null);
    setSection(nextTab === "projects" ? nextSection : null);
    setContractorId(nextTab === "payables" ? nextDetailId : null);
    setClientId(nextTab === "billing" ? nextDetailId : null);
    setEstimateId(nextTab === "estimates" ? nextDetailId : null);
    setContractId(nextTab === "proposals" || nextTab === "contracts" ? nextDetailId : null);
  }

  /** Every cross-link into a proposal or a contract goes through here, so the
   *  caller only has to say which kind rather than which tab that kind lives
   *  under. */
  const openContract = (id: string, kind: ContractDocKind) => {
    go(active.slug, kind === "contract" ? "contracts" : "proposals", id);
  };
  const closeContract = (kind: ContractDocKind) => {
    go(active.slug, kind === "contract" ? "contracts" : "proposals");
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      {/* Unmissable on purpose. An operator looking at someone else's books
          should never have to work out whose data is on screen — and every
          request made from here is written to that company's audit log. */}
      {visiting && (
        <div className="bg-amber-500 px-6 py-1.5 text-center text-sm font-medium text-amber-950">
          Super-user access — you are not a member of {active.name}. This visit is being logged.
        </div>
      )}

      <header className="sticky top-0 z-30 h-14 border-b border-neutral-200 bg-white/85 backdrop-blur-md">
        <GlobalProgressBar />
        <div className="mx-auto flex h-full max-w-[1600px] items-center gap-4 px-6">
          <span className="whitespace-nowrap font-[family-name:--font-display] text-lg font-bold tracking-tight text-neutral-900">
            Dantria
          </span>

          <select
            className="min-w-0 rounded-lg border border-neutral-300 bg-white px-2 py-1 text-sm shadow-sm outline-none transition-colors focus:border-accent-500 focus:ring-2 focus:ring-accent-500/25"
            value={active.slug}
            onChange={(e) => {
              if (e.target.value === NEW_COMPANY) setCreating(true);
              else go(e.target.value, tab);
            }}
          >
            {companies.map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
                {!memberOf.some((m) => m.id === c.id) ? " ·" : ""}
              </option>
            ))}
            {/* The only route to company creation in the UI, and it is only
                rendered for the only role the API accepts it from. */}
            {isSuper && <option value={NEW_COMPANY}>+ New company…</option>}
          </select>

          <span className="ml-auto truncate text-sm text-neutral-500">{me.data.user.email}</span>
          <SignOutButton queryClient={queryClient} className="whitespace-nowrap" />
        </div>
      </header>

      <div className="mx-auto flex max-w-[1600px]">
        <nav className="sticky top-14 h-[calc(100vh-3.5rem)] w-56 shrink-0 overflow-y-auto border-r border-neutral-200 px-3 py-6">
          <div className="flex flex-col gap-0.5">
            {TABS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => go(active.slug, t)}
                aria-current={tab === t ? "page" : undefined}
                className={`rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  tab === t
                    ? "bg-accent-50 font-medium text-accent-700"
                    : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                }`}
              >
                {TAB_LABELS[t]}
              </button>
            ))}
          </div>

          {/* Platform administration, kept visually apart from the company's
              own nav: it is not this company's screen, and an operator should
              never be unsure which of the two they are looking at. */}
          {isSuper && (
            <div className="mt-6 border-t border-neutral-200 pt-4">
              <button
                type="button"
                onClick={() => {
                  setAdmin(true);
                  window.history.pushState({}, "", "/admin");
                }}
                className="w-full rounded-lg px-3 py-2 text-left text-sm text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
              >
                Accounts &amp; companies
              </button>
            </div>
          )}
        </nav>

        <main className="min-w-0 flex-1 px-6 mt-3">
          <Page
            tab={tab}
            company={active}
            projectId={projectId}
            section={section}
            contractorId={contractorId}
            clientId={clientId}
            estimateId={estimateId}
            contractId={contractId}
            onOpenProject={(id) => go(active.slug, "projects", id)}
            onCloseProject={() => go(active.slug, "projects")}
            onSection={(s) => go(active.slug, "projects", projectId, s)}
            onOpenContractor={(id) => go(active.slug, "payables", id)}
            onCloseContractor={() => go(active.slug, "payables")}
            onOpenClient={(id) => go(active.slug, "billing", id)}
            onCloseClient={() => go(active.slug, "billing")}
            onOpenEstimate={(id) => go(active.slug, "estimates", id)}
            onCloseEstimate={() => go(active.slug, "estimates")}
            onOpenContract={openContract}
            onCloseContract={closeContract}
          />
        </main>
      </div>
    </div>
  );
}

function Page({
  tab,
  company,
  projectId,
  section,
  contractorId,
  clientId,
  estimateId,
  contractId,
  onOpenProject,
  onCloseProject,
  onSection,
  onOpenContractor,
  onCloseContractor,
  onOpenClient,
  onCloseClient,
  onOpenEstimate,
  onCloseEstimate,
  onOpenContract,
  onCloseContract,
}: {
  tab: Tab;
  company: CompanySummary;
  projectId: string | null;
  section: string | null;
  contractorId: string | null;
  clientId: string | null;
  estimateId: string | null;
  contractId: string | null;
  onOpenProject: (id: string) => void;
  onCloseProject: () => void;
  onSection: (section: string) => void;
  onOpenContractor: (id: string) => void;
  onCloseContractor: () => void;
  onOpenClient: (id: string) => void;
  onCloseClient: () => void;
  onOpenEstimate: (id: string) => void;
  onCloseEstimate: () => void;
  onOpenContract: (id: string, kind: ContractDocKind) => void;
  onCloseContract: (kind: ContractDocKind) => void;
}) {
  // Deleting a record is owner-only, so every page that lists one needs to know
  // which role is looking at it. The API refuses it either way — this only
  // decides whether the button is worth showing.
  switch (tab) {
    case "reports":
      return <Reports slug={company.slug} currency={company.baseCurrency} />;
    case "projects":
      // Keyed by project: switching between two of them must not carry one
      // project's open forms and filters over to the next.
      return projectId ? (
        <ProjectDetail
          key={projectId}
          slug={company.slug}
          projectId={projectId}
          role={company.role}
          section={section}
          onSection={onSection}
          onBack={onCloseProject}
          // A project's own paperwork is always the binding contract — only a
          // contract approves into projects, never a proposal.
          onOpenContract={(id) => onOpenContract(id, "contract")}
        />
      ) : (
        <Projects
          slug={company.slug}
          currency={company.baseCurrency}
          role={company.role}
          onOpenProject={onOpenProject}
          onOpenContract={(id) => onOpenContract(id, "contract")}
        />
      );
    case "billing":
      // Keyed by client for the same reason a project or a contractor is:
      // switching which one is open must not carry the last one's picked
      // project or open forms over.
      return clientId ? (
        <ClientDetail
          key={clientId}
          slug={company.slug}
          clientId={clientId}
          currency={company.baseCurrency}
          role={company.role}
          onBack={onCloseClient}
        />
      ) : (
        <Billing
          slug={company.slug}
          currency={company.baseCurrency}
          role={company.role}
          onOpenClient={onOpenClient}
        />
      );
    case "payables":
      // Keyed by contractor for the same reason a project is: switching which
      // one is open must not carry the last one's expanded rows and forms over.
      return contractorId ? (
        <ContractorDetail
          key={contractorId}
          slug={company.slug}
          contractorId={contractorId}
          currency={company.baseCurrency}
          role={company.role}
          onBack={onCloseContractor}
        />
      ) : (
        <Payables
          slug={company.slug}
          currency={company.baseCurrency}
          role={company.role}
          onOpenContractor={onOpenContractor}
        />
      );
    case "estimates":
      // Keyed by estimate for the same reason a project or a contractor is:
      // switching which one is open must not carry the last one's open forms
      // and chosen packages over.
      return estimateId ? (
        <EstimateDetail
          key={estimateId}
          slug={company.slug}
          estimateId={estimateId}
          currency={company.baseCurrency}
          role={company.role}
          onBack={onCloseEstimate}
          // An estimate's own paperwork is the proposal built from it, never
          // the contract that proposal might later convert into.
          onOpenContract={(id) => onOpenContract(id, "proposal")}
          onOpenProject={onOpenProject}
        />
      ) : (
        <Estimates
          slug={company.slug}
          currency={company.baseCurrency}
          role={company.role}
          onOpenEstimate={onOpenEstimate}
          onOpenContract={(id) => onOpenContract(id, "proposal")}
        />
      );
    case "proposals":
      // Keyed by contract, same as an estimate: switching which proposal is
      // open must not carry the last one's unsaved document over.
      return contractId ? (
        <ContractDetail
          key={contractId}
          slug={company.slug}
          contractId={contractId}
          kind="proposal"
          role={company.role}
          onBack={() => onCloseContract("proposal")}
          onOpenProject={onOpenProject}
          onOpenEstimate={onOpenEstimate}
          onOpenContract={onOpenContract}
        />
      ) : (
        <Contracts
          slug={company.slug}
          kind="proposal"
          currency={company.baseCurrency}
          role={company.role}
          onOpenContract={(id) => onOpenContract(id, "proposal")}
          onOpenEstimate={onOpenEstimate}
        />
      );
    case "contracts":
      // Keyed by contract, same reason as a proposal.
      return contractId ? (
        <ContractDetail
          key={contractId}
          slug={company.slug}
          contractId={contractId}
          kind="contract"
          role={company.role}
          onBack={() => onCloseContract("contract")}
          onOpenProject={onOpenProject}
          onOpenEstimate={onOpenEstimate}
          onOpenContract={onOpenContract}
        />
      ) : (
        <Contracts
          slug={company.slug}
          kind="contract"
          currency={company.baseCurrency}
          role={company.role}
          onOpenContract={(id) => onOpenContract(id, "contract")}
          onOpenEstimate={onOpenEstimate}
        />
      );
    case "commissions":
      return (
        <Commissions slug={company.slug} currency={company.baseCurrency} role={company.role} />
      );
    case "expenses":
      return <Expenses slug={company.slug} currency={company.baseCurrency} role={company.role} />;
    case "clients":
      return (
        <Clients
          slug={company.slug}
          currency={company.baseCurrency}
          role={company.role}
          onOpenProject={onOpenProject}
        />
      );
    case "contractors":
      return (
        <Contractors slug={company.slug} role={company.role} onOpenContractor={onOpenContractor} />
      );
    case "cost-codes":
      return <CostCodes slug={company.slug} role={company.role} />;
    case "settings":
      return <Settings slug={company.slug} role={company.role} currency={company.baseCurrency} />;
  }
}

function SignOutButton({
  queryClient,
  className = "",
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={async () => {
        await signOut();
        // Drop every cached tenant response, not just the session — the next
        // person to sign in on this machine must not see them.
        queryClient.clear();
      }}
      className={`text-sm text-neutral-600 underline underline-offset-4 hover:text-neutral-900 ${className}`}
    >
      Sign out
    </button>
  );
}

/**
 * A thin bar across the top of the header, lit whenever any request is in
 * flight — a query loading data or a mutation saving it. React Query already
 * tracks every one of those centrally, so this is the one place that needs to
 * know about them; a page that adds a new save button gets covered for free.
 */
function GlobalProgressBar() {
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  if (fetching + mutating === 0) return null;

  return (
    <div className="absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-transparent">
      <div className="absolute h-full w-1/3 animate-[progress-sweep_1.1s_ease-in-out_infinite] bg-accent-500" />
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50">
      <div className="text-center">{children}</div>
    </div>
  );
}
